/* =========================================================================
   Public shared-regimen page controller.

   Depends on assets/regimen.js (window.LazRegimen).

   Notes shown here belong to the sharer and were opted in explicitly. They
   are still rendered with textContent — the Worker stores them raw by design.
   ========================================================================= */

(function () {
  "use strict";

  if (customElements.get("laz-shared-regimen")) return;

  const {
    api,
    fetchRows,
    activesOf,
    groupByStep,
    scanInteractions,
    renderInteractions,
    el,
    toast,
  } = window.LazRegimen;

  class LazSharedRegimen extends HTMLElement {
    connectedCallback() {
      this.loggedIn = this.dataset.loggedIn === "true";
      this.returnPath = this.dataset.returnPath || "/pages/shared-regimen";

      this.statusEl = this.querySelector(".js-status");
      this.routineSection = this.querySelector(".js-routine-section");
      this.routineEl = this.querySelector(".js-routine");
      this.routineCountEl = this.querySelector(".js-routine-count");
      this.wishlistSection = this.querySelector(".js-wishlist-section");
      this.wishlistEl = this.querySelector(".js-wishlist");
      this.wishlistCountEl = this.querySelector(".js-wishlist-count");
      this.interactionsEl = this.querySelector(".js-interactions");

      this.copySection = this.querySelector(".js-copy");
      this.copyWishlistWrap = this.querySelector(".js-copy-wishlist-wrap");
      this.copyWishlist = this.querySelector(".js-copy-wishlist");
      this.copySubmit = this.querySelector(".js-copy-submit");

      this.token = new URLSearchParams(location.search).get("s");

      this._load();
      this.copySubmit.addEventListener("click", () => this._copy());
    }

    async _load() {
      if (!this.token) {
        this._notFound();
        return;
      }

      let data;
      try {
        data = await api.get(`shared?s=${encodeURIComponent(this.token)}`);
      } catch (err) {
        if (err.status === 429) {
          this._status("This regimen is getting a lot of views right now. Please try again in a minute.");
        } else {
          this._notFound();
        }
        return;
      }

      this.data = data;
      this.statusEl.hidden = true;

      await this._renderRoutine(data.routine.items);
      if (data.wishlist) await this._renderWishlist(data.wishlist.items);

      this.copySection.hidden = false;
      this.copyWishlistWrap.hidden = !data.has_wishlist;
    }

    _notFound() {
      this.statusEl.textContent = "";
      const wrap = el("div", "regimen-shared__notfound");
      wrap.appendChild(
        el("p", "text-base", "This link isn't active anymore.")
      );
      wrap.appendChild(
        el(
          "p",
          "text-sm text-opacity",
          "The person who shared it may have turned sharing off, or the link may be incomplete."
        )
      );
      const link = el("a", "button button--secondary", null);
      link.href = "/collections/all";
      link.appendChild(el("span", "btn-text", "Browse products"));
      wrap.appendChild(link);
      this.statusEl.appendChild(wrap);
    }

    _status(message) {
      this.statusEl.textContent = "";
      this.statusEl.appendChild(el("p", "text-sm", message));
    }

    async _renderRoutine(items) {
      if (!items.length) {
        this._status("This regimen is empty right now.");
        return;
      }

      this.routineSection.hidden = false;
      this.routineCountEl.textContent = `${items.length} product${items.length === 1 ? "" : "s"}`;

      const entries = await fetchRows(items);
      const groups = groupByStep(entries);

      this.routineEl.textContent = "";

      for (const [stepLabel, groupEntries] of groups) {
        const group = el("div", "regimen-group");
        group.appendChild(
          el("h3", "regimen-group__label text-sm font-medium text-opacity", stepLabel)
        );

        const list = el("ul", "regimen-group__list");
        list.setAttribute("role", "list");

        for (const { item, node } of groupEntries) {
          const li = el("li", "regimen-row regimen-row--readonly");

          if (!node) {
            li.appendChild(
              el("p", "regimen-row__missing text-sm text-opacity", "This product is no longer available.")
            );
          } else {
            li.appendChild(node);
            if (item.notes) {
              const slot = node.querySelector("[data-slot]");
              const note = el("p", "regimen-row__notes-text regimen-row__notes-text--shared text-sm");
              note.textContent = item.notes; // sharer's text, escaped by textContent
              slot.appendChild(note);
            }
          }

          list.appendChild(li);
        }

        group.appendChild(list);
        this.routineEl.appendChild(group);
      }

      const withActives = entries
        .filter((e) => e.node)
        .map((e) => ({
          title: e.node.querySelector(".regimen-row__title")?.textContent.trim() || "",
          actives: activesOf(e.node),
        }));
      renderInteractions(scanInteractions(withActives), this.interactionsEl);
    }

    async _renderWishlist(items) {
      if (!items.length) return;

      this.wishlistSection.hidden = false;
      this.wishlistCountEl.textContent = `${items.length} product${items.length === 1 ? "" : "s"}`;

      const entries = await fetchRows(items);
      const list = el("ul", "regimen-group__list");
      list.setAttribute("role", "list");

      for (const { node } of entries) {
        const li = el("li", "regimen-row regimen-row--readonly");
        if (node) li.appendChild(node);
        else
          li.appendChild(
            el("p", "regimen-row__missing text-sm text-opacity", "This product is no longer available.")
          );
        list.appendChild(li);
      }

      this.wishlistEl.textContent = "";
      this.wishlistEl.appendChild(list);
    }

    _selectedMode() {
      const checked = this.querySelector('input[name="copy-mode"]:checked');
      return checked ? checked.value : "merge";
    }

    async _copy() {
      const mode = this._selectedMode();
      const includeWishlist = this.copyWishlist.checked;

      /* Logged out: stash the intent and resume after login. sessionStorage
         rather than localStorage so an abandoned intent doesn't linger. */
      if (!this.loggedIn) {
        try {
          sessionStorage.setItem(
            "laz_pending_copy",
            JSON.stringify({ token: this.token, mode, include_wishlist: includeWishlist })
          );
        } catch {
          /* private mode — the copy simply won't resume */
        }
        const target = encodeURIComponent("/pages/regimen?resume_copy=1");
        location.href = `/account/login?return_url=${target}`;
        return;
      }

      if (mode === "replace") {
        const ok = confirm(
          "This will clear your current regimen and replace it with this one. Continue?"
        );
        if (!ok) return;
      }

      this.copySubmit.disabled = true;

      try {
        const res = await api.post("copy", {
          token: this.token,
          mode,
          include_wishlist: includeWishlist,
        });

        const count = res.added;
        toast(
          mode === "replace"
            ? "Saved. Your regimen has been replaced."
            : `Saved. ${count} product${count === 1 ? "" : "s"} added.`
        );

        setTimeout(() => {
          location.href = "/pages/regimen";
        }, 1200);
      } catch (err) {
        this.copySubmit.disabled = false;
        toast(err.body?.error || "We couldn't save this regimen.", "error");
      }
    }
  }

  customElements.define("laz-shared-regimen", LazSharedRegimen);
})();